package com.taocent.simple.compose.component.richtext.core.internal.serialization

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.ParagraphStyle
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.BaselineShift
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.sp
import com.taocent.simple.compose.component.richtext.core.internal.emoji.CUSTOM_EMOJI_PLACEHOLDER
import com.taocent.simple.compose.component.richtext.core.internal.emoji.CUSTOM_EMOJI_TAG

/**
 * 迷你 HTML → AnnotatedString 解析器。
 *
 * 仅支持本组件富文本粘贴场景下需要的 HTML 子集:
 *   - 语义标签:`<b>/<strong>` `<i>/<em>` `<u>/<ins>` `<s>/<strike>/<del>`
 *     `<sup>/<sub>` `<code>` `<mark>` `<small>` → `SpanStyle`
 *   - 段落容器:`<p>/<div>` 段间换行;`<p style="text-align:center">` 还原 `ParagraphStyle`
 *   - 内联样式:`<span style="color/font-size/font-weight/font-style/text-decoration/background">` → `SpanStyle`
 *   - 超链接:`<a href="url">` → `StringAnnotation(URL, url)`
 *   - emoji 占位:`<span data-emoji-id="id">[id]</span>` → text 含 `[id]` 短代码,
 *     `StringAnnotation(CUSTOM_EMOJI, id)` 范围 = `[id]` 文字范围(本组件 emoji
 *     渲染用 [com.taocent.simple.compose.component.richtext.core.internal.ui.CustomEmojiCanvasOverlay]
 *     在 text 上覆盖绘制,不依赖 `█` placeholder 字符)
 *   - 换行:`<br>` → `\n`
 *   - HTML 实体:`&amp;` `&lt;` `&gt;` `&quot;` `&apos;` `&nbsp;` 等 → 原文
 *
 * 容错:未知标签 / 属性 / 未闭合标签,只保留文本;多个空白合并为单个空格。
 *
 * 与 [AnnotatedStringToHtml] 配对使用,实现 text/html ↔ AnnotatedString 双向序列化。
 * 调用方一般通过 [com.taocent.simple.compose.component.richtext.core.internal.clipboard.clipboardGetText]
 * 在 web/JVM/Android/iOS 平台 actual 实现中读取 `text/html` 后调 [parse]。
 */
internal object HtmlToAnnotatedString {

    fun parse(html: String): AnnotatedString {
        val builder = AnnotatedString.Builder()
        val styleStack = mutableListOf<StyleEntry>()
        // 段落级样式栈 —— 每个 `<p style="text-align:...">` 推入,闭合 `<p>` 时弹出
        val paragraphStyleStack = mutableListOf<ParagraphStyle>()
        // 超链接跟踪:`<a>` open 时压入 (href, start),`</a>` close 时弹栈并用 [start, builder.length) 落 annotation
        val hyperlinkStack = mutableListOf<Pair<String, Int>>()
        var i = 0
        // 修复:去除开头的 UTF-8 BOM (\uFEFF) 和 `<meta charset>` 声明。
        // 背景:序列化器 [AnnotatedStringToHtml.toHtml] 会在 HTML 头部输出
        // `\uFEFF<meta charset="UTF-8">` 以避免 macOS 备忘录按 GBK 解析中文乱码。
        // 这里必须把 BOM 字符和 meta 标签一并跳过,否则 parse 后会多出 BOM 字符,
        // 且 meta 标签进入 styleStack 后留下未配对的空栈帧(无实际影响但不干净)。
        var text = html.trim()
        if (text.isNotEmpty() && text[0] == '\uFEFF') {
            text = text.substring(1)
        }
        // 跳过开头的 `<meta ...>` 标签(只接受 self-closing 或显式闭合,必须是首段标签)
        val metaRegex = Regex("""^<meta\s+[^>]*>""", RegexOption.IGNORE_CASE)
        text = metaRegex.replace(text, "")

        while (i < text.length) {
            if (text[i] == '<') {
                val closeIdx = text.indexOf('>', i)
                if (closeIdx == -1) {
                    builder.append(text[i])
                    i++
                    continue
                }
                val tagContent = text.substring(i + 1, closeIdx).trim()
                val isClosing = tagContent.startsWith("/")
                val tagBody = if (isClosing) tagContent.substring(1).trim() else tagContent

                if (isClosing) {
                    val tagName = tagBody.substringBefore(' ').lowercase()
                    val idx = styleStack.indexOfLast { it.tagName == tagName }
                    if (idx >= 0) {
                        val entry = styleStack.removeAt(idx)
                        entry.style?.let { builder.pop() }
                        if (entry.isHyperlink) {
                            // 关闭 hyperlink:用 start..builder.length 落 annotation
                            // hyperlinkStack 末尾一定对应这个 href(单层)
                            if (hyperlinkStack.isNotEmpty()) {
                                val (href, hrefStart) = hyperlinkStack.removeAt(hyperlinkStack.lastIndex)
                                if (hrefStart < builder.length) {
                                    builder.addStringAnnotation("URL", href, hrefStart, builder.length)
                                }
                            }
                        }
                    }
                    if (tagName == "p" && paragraphStyleStack.isNotEmpty()) {
                        paragraphStyleStack.removeAt(paragraphStyleStack.lastIndex)
                        if (paragraphStyleStack.isNotEmpty()) {
                            builder.pushStyle(paragraphStyleStack.last())
                        } else {
                            builder.pop()
                        }
                    }
                } else {
                    val tagName = tagBody.substringBefore(' ').lowercase()
                    val attrs = parseAttributes(tagBody)
                    val style = tagToStyle(tagName, attrs)
                    val isHyperlink = tagName == "a"
                    val isVoid = tagName in listOf("br", "hr", "img")

                    if (tagName == "br") {
                        builder.append('\n')
                    } else if (tagName == "p" || tagName == "div") {
                        if (builder.length > 0 && !endsWithNewline(builder)) {
                            builder.append('\n')
                        }
                        // 解析 `<p style="text-align:...">` 的段落级样式
                        val paragraphStyle = parseParagraphStyle(attrs)
                        if (paragraphStyle != null) {
                            paragraphStyleStack.add(paragraphStyle)
                            builder.pushStyle(paragraphStyle)
                        }
                    } else if (!isVoid) {
                        if (isHyperlink) {
                            val href = attrs["href"] ?: ""
                            // 记录 hyperlink 开始位置 + href,延迟到 `</a>` 关闭时再 addStringAnnotation
                            hyperlinkStack.add(href to builder.length)
                        }
                        // 解析 `<span data-emoji-id="...">` 为 emoji annotation
                        // 内存存储约定(参见 [insertTextWithEmojis]):
                        //   emoji 在内存中用 `█` (`CUSTOM_EMOJI_PLACEHOLDER`) 占位符 + StringAnnotation
                        //   范围覆盖该占位符字符 —— 渲染层 CustomEmojiPainter 看到 `█` + annotation 才画
                        //   emoji 图像,如果用 `[id]` shortcode 文本就只会显示原文,不渲染。
                        // 因此反序列化时必须 append `█` placeholder(而不是 `[id]`),
                        // 保证与内存模型一致,粘贴后 emoji 立即可见。
                        //
                        // **emoji 样式必须 pushStyle 之后才 append placeholder**:
                        // 序列化器 [AnnotatedStringToHtml] 把 emoji 段的 SpanStyle 合并到
                        // outer `<span data-emoji-id style="...">` 的 `style` 属性(不再额外
                        // 开内层 `<span style>`),这里必须先 pushStyle 再 append placeholder,
                        // 让 placeholder 字符被 SpanStyle 覆盖,emoji 渲染时(`CustomEmojiCanvasOverlay`)
                        // 能从 spanStyles 拿到 emoji 段的 fontSize / color 等样式。如果先 append
                        // placeholder 再 pushStyle,placeholder 字符就不在 SpanStyle 覆盖范围,
                        // **emoji 跨进程 paste 后样式丢失**。
                        if (tagName == "span" && attrs.containsKey("data-emoji-id")) {
                            val emojiId = attrs["data-emoji-id"] ?: ""
                            if (emojiId.isNotEmpty()) {
                                val emojiStart = builder.length
                                // 解析 outer `<span data-emoji-id style="...">` 上的 span style
                                val emojiSpan = parseSpanStyle(attrs)
                                emojiSpan?.let { builder.pushStyle(it) }
                                builder.append(CUSTOM_EMOJI_PLACEHOLDER)
                                builder.addStringAnnotation(
                                    CUSTOM_EMOJI_TAG,
                                    emojiId,
                                    emojiStart,
                                    builder.length
                                )
                                emojiSpan?.let { builder.pop() }
                            }
                            // emoji 段 push/pop 已在上面完成,styleStack 不应再持有 style:
                            // close tag 时 `entry.style?.let { builder.pop() }` 会基于这个
                            // style 再次 pop,导致 push/pop 失衡("Nothing to pop")。记录
                            // `style = null` 让 close tag 不重复 pop。
                            styleStack.add(StyleEntry(tagName, null, isHyperlink))
                        } else {
                            // 非 emoji 段的 `<span style>` 走原路径
                            style?.let { builder.pushStyle(it) }
                            styleStack.add(StyleEntry(tagName, style, isHyperlink))
                        }
                    }
                }
                i = closeIdx + 1
            } else if (text[i] == '&') {
                val (entity, endIdx) = decodeEntity(text, i)
                builder.append(entity)
                i = endIdx
            } else {
                builder.append(text[i])
                i++
            }
        }

        for (entry in styleStack.reversed()) {
            entry.style?.let { builder.pop() }
        }

        return builder.toAnnotatedString()
    }

    /**
     * 解析 `<p style="text-align:center/left/right/justify">` 的段落样式。
     * 当前仅支持 `text-align` —— 本组件 paragraph style 主要就是 text-align。
     * 返回 `null` 时调用方不推入 paragraph style stack(等价于无段落样式)。
     */
    private fun parseParagraphStyle(attrs: Map<String, String>): ParagraphStyle? {
        val style = attrs["style"] ?: return null
        var textAlign: TextAlign? = null
        val declarations = style.split(';').map { it.trim() }.filter { it.isNotEmpty() }
        for (decl in declarations) {
            val colonIdx = decl.indexOf(':')
            if (colonIdx < 0) continue
            val prop = decl.substring(0, colonIdx).trim().lowercase()
            val value = decl.substring(colonIdx + 1).trim()
            if (prop == "text-align") {
                textAlign = when (value.lowercase()) {
                    "left" -> TextAlign.Left
                    "right" -> TextAlign.Right
                    "center" -> TextAlign.Center
                    "justify" -> TextAlign.Justify
                    "start" -> TextAlign.Start
                    "end" -> TextAlign.End
                    else -> null
                }
                if (textAlign != null) break
            }
        }
        return if (textAlign != null) ParagraphStyle(textAlign = textAlign) else null
    }

    private fun tagToStyle(tag: String, attrs: Map<String, String>): SpanStyle? {
        val base = when (tag) {
            "b", "strong" -> SpanStyle(fontWeight = FontWeight.Bold)
            "i", "em" -> SpanStyle(fontStyle = FontStyle.Italic)
            "u", "ins" -> SpanStyle(textDecoration = TextDecoration.Underline)
            "s", "strike", "del" -> SpanStyle(textDecoration = TextDecoration.LineThrough)
            "sup" -> SpanStyle(baselineShift = BaselineShift.Superscript)
            "sub" -> SpanStyle(baselineShift = BaselineShift.Subscript)
            "code" -> SpanStyle(
                background = Color(0x1A808080),
                fontSize = 14.sp
            )
            "mark" -> SpanStyle(background = Color(0xFFFFFF00))
            "small" -> SpanStyle(fontSize = 12.sp)
            "span" -> parseSpanStyle(attrs)
            else -> return null
        }
        return base
    }

    private fun parseSpanStyle(attrs: Map<String, String>): SpanStyle? {
        val style = attrs["style"] ?: return null
        var result = SpanStyle()
        val declarations = style.split(';').map { it.trim() }.filter { it.isNotEmpty() }
        for (decl in declarations) {
            val colonIdx = decl.indexOf(':')
            if (colonIdx < 0) continue
            val prop = decl.substring(0, colonIdx).trim().lowercase()
            val value = decl.substring(colonIdx + 1).trim()
            when (prop) {
                "font-weight" -> {
                    val weight = when (value.lowercase()) {
                        "bold", "bolder" -> FontWeight.Bold
                        "lighter" -> FontWeight.Light
                        "normal" -> FontWeight.Normal
                        else -> value.toIntOrNull()?.let { FontWeight(it) }
                    }
                    weight?.let { result = result.copy(fontWeight = it) }
                }
                "font-style" -> {
                    if (value.lowercase() == "italic") {
                        result = result.copy(fontStyle = FontStyle.Italic)
                    }
                }
                "text-decoration" -> {
                    val decorations = mutableListOf<TextDecoration>()
                    if (value.contains("underline")) decorations.add(TextDecoration.Underline)
                    if (value.contains("line-through")) decorations.add(TextDecoration.LineThrough)
                    if (decorations.isNotEmpty()) {
                        result = result.copy(textDecoration = TextDecoration.combine(decorations))
                    }
                }
                "color" -> {
                    parseCssColor(value)?.let { result = result.copy(color = it) }
                }
                "background-color", "background" -> {
                    parseCssColor(value)?.let { result = result.copy(background = it) }
                }
                "font-size" -> {
                    parseCssFontSize(value)?.let { result = result.copy(fontSize = it) }
                }
                "vertical-align" -> {
                    when (value.lowercase()) {
                        "super", "sup" -> result = result.copy(baselineShift = BaselineShift.Superscript)
                        "sub" -> result = result.copy(baselineShift = BaselineShift.Subscript)
                    }
                }
            }
        }
        return if (result == SpanStyle()) null else result
    }

    private fun parseCssColor(value: String): Color? {
        val v = value.trim().lowercase()
        return when {
            v.startsWith("#") -> {
                val hex = v.removePrefix("#")
                try {
                    when (hex.length) {
                        3 -> {
                            val r = "${hex[0]}${hex[0]}".toInt(16)
                            val g = "${hex[1]}${hex[1]}".toInt(16)
                            val b = "${hex[2]}${hex[2]}".toInt(16)
                            Color(r, g, b)
                        }
                        6 -> {
                            val r = hex.substring(0, 2).toInt(16)
                            val g = hex.substring(2, 4).toInt(16)
                            val b = hex.substring(4, 6).toInt(16)
                            Color(r, g, b)
                        }
                        8 -> {
                            val a = hex.substring(0, 2).toInt(16)
                            val r = hex.substring(2, 4).toInt(16)
                            val g = hex.substring(4, 6).toInt(16)
                            val b = hex.substring(6, 8).toInt(16)
                            // Compose `Color(r, g, b, a)` 顺序:r/g/b 在前,alpha 在最后
                            Color(r, g, b, a)
                        }
                        else -> null
                    }
                } catch (_: Exception) { null }
            }
            v.startsWith("rgb(") -> {
                val parts = v.removePrefix("rgb(").removeSuffix(")")
                    .split(",").map { it.trim().removeSuffix("%").trim().toFloatOrNull() }
                if (parts.size >= 3 && parts.all { it != null }) {
                    Color(parts[0]!!.toInt(), parts[1]!!.toInt(), parts[2]!!.toInt())
                } else null
            }
            v.startsWith("rgba(") -> {
                val parts = v.removePrefix("rgba(").removeSuffix(")")
                    .split(",").map { it.trim() }
                if (parts.size >= 4) {
                    val r = parts[0].toFloatOrNull()?.toInt()
                    val g = parts[1].toFloatOrNull()?.toInt()
                    val b = parts[2].toFloatOrNull()?.toInt()
                    val a = (parts[3].toFloatOrNull()?.times(255))?.toInt()
                    if (r != null && g != null && b != null && a != null) {
                        Color(r, g, b, a)
                    } else null
                } else null
            }
            else -> NAMED_COLORS[v]
        }
    }

    private fun parseCssFontSize(value: String): TextUnit? {
        val v = value.trim().lowercase()
        return when {
            v.endsWith("px") -> v.removeSuffix("px").toFloatOrNull()?.let { it.sp }
            v.endsWith("pt") -> v.removeSuffix("pt").toFloatOrNull()?.let { it.sp }
            v.endsWith("em") -> v.removeSuffix("em").toFloatOrNull()?.let { (it * 16).sp }
            v.endsWith("rem") -> v.removeSuffix("rem").toFloatOrNull()?.let { (it * 16).sp }
            v.endsWith("%") -> v.removeSuffix("%").toFloatOrNull()?.let { (it / 100 * 16).sp }
            else -> v.toFloatOrNull()?.let { it.sp }
        }
    }

    private fun parseAttributes(tagBody: String): Map<String, String> {
        val attrs = mutableMapOf<String, String>()
        val body = tagBody.substringAfter(' ', "")
        var i = 0
        while (i < body.length) {
            while (i < body.length && body[i].isWhitespace()) i++
            if (i >= body.length) break
            val nameStart = i
            while (i < body.length && body[i] != '=' && !body[i].isWhitespace()) i++
            val name = body.substring(nameStart, i).lowercase()
            while (i < body.length && body[i].isWhitespace()) i++
            if (i < body.length && body[i] == '=') {
                i++
                while (i < body.length && body[i].isWhitespace()) i++
                val value = if (i < body.length && (body[i] == '"' || body[i] == '\'')) {
                    val quote = body[i]
                    i++
                    val valStart = i
                    while (i < body.length && body[i] != quote) i++
                    val v = body.substring(valStart, i)
                    if (i < body.length) i++
                    v
                } else {
                    val valStart = i
                    while (i < body.length && !body[i].isWhitespace()) i++
                    body.substring(valStart, i)
                }
                attrs[name] = value
            } else {
                attrs[name] = ""
            }
        }
        return attrs
    }

    private fun decodeEntity(text: String, start: Int): Pair<String, Int> {
        val semiIdx = text.indexOf(';', start)
        if (semiIdx == -1 || semiIdx - start > 10) return Pair("&", start + 1)
        val entity = text.substring(start + 1, semiIdx)
        val decoded = when (entity) {
            "amp" -> "&"
            "lt" -> "<"
            "gt" -> ">"
            "quot" -> "\""
            "apos" -> "'"
            "nbsp" -> "\u00A0"
            "mdash" -> "\u2014"
            "ndash" -> "\u2013"
            "hellip" -> "\u2026"
            "copy" -> "\u00A9"
            "reg" -> "\u00AE"
            "trade" -> "\u2122"
            "euro" -> "\u20AC"
            "pound" -> "\u00A3"
            "yen" -> "\u00A5"
            "cent" -> "\u00A2"
            "deg" -> "\u00B0"
            "plusmn" -> "\u00B1"
            "times" -> "\u00D7"
            "divide" -> "\u00F7"
            else -> {
                if (entity.startsWith("#x")) {
                    entity.substring(2).toIntOrNull(16)?.toChar()?.toString()
                } else if (entity.startsWith("#")) {
                    entity.substring(1).toIntOrNull()?.toChar()?.toString()
                } else null
            }
        }
        return Pair(decoded ?: "&$entity;", semiIdx + 1)
    }

    private fun endsWithNewline(builder: AnnotatedString.Builder): Boolean {
        // 多次 `toAnnotatedString()` 性能 O(n) 累积,仅 paragraph 边界检查使用 ——
        // 复杂度 O(n²),但 n 通常极小(一个段落)且只触发一次 per `<p>`;长文可通过
        // 改造 `AnnotatedString.Builder` 暴露 length check 来避免。
        return builder.length > 0 && builder.toAnnotatedString().text.last() == '\n'
    }

    private data class StyleEntry(
        val tagName: String,
        val style: SpanStyle?,
        val isHyperlink: Boolean = false
    )

    private val NAMED_COLORS = mapOf(
        "black" to Color(0xFF000000),
        "white" to Color(0xFFFFFFFF),
        "red" to Color(0xFFFF0000),
        "green" to Color(0xFF008000),
        "blue" to Color(0xFF0000FF),
        "yellow" to Color(0xFFFFFF00),
        "cyan" to Color(0xFF00FFFF),
        "magenta" to Color(0xFFFF00FF),
        "gray" to Color(0xFF808080),
        "grey" to Color(0xFF808080),
        "silver" to Color(0xFFC0C0C0),
        "maroon" to Color(0xFF800000),
        "olive" to Color(0xFF808000),
        "lime" to Color(0xFF00FF00),
        "aqua" to Color(0xFF00FFFF),
        "teal" to Color(0xFF008080),
        "navy" to Color(0xFF000080),
        "fuchsia" to Color(0xFFFF00FF),
        "purple" to Color(0xFF800080),
        "orange" to Color(0xFFFFA500),
        "pink" to Color(0xFFFFC0CB),
        "brown" to Color(0xFFA52A2A),
        "coral" to Color(0xFFFF7F50),
        "gold" to Color(0xFFFFD700),
        "indigo" to Color(0xFF4B0082),
        "khaki" to Color(0xFFF0E68D),
        "lavender" to Color(0xFFE6E6FA),
        "tomato" to Color(0xFFFF6347),
        "turquoise" to Color(0xFF40E0D0),
        "violet" to Color(0xFFEE82EE),
        "wheat" to Color(0xFFF5DEB3)
    )
}
